<script setup>
/**
 * DEV-ONLY: replay the first-run guided tour.
 *
 * The tour is once-per-account (`data.about.new_design_tour_seen`), which makes
 * it awkward to work on — once you've seen it, you can't see it again without
 * editing the flag by hand. This flips that same flag, so there's no test-only
 * state that could drift from what production reads.
 *
 * Never rendered in production. On localhost it is open to whoever is running
 * the app; on staging it is limited to the people who actually work on the
 * tour, so a dealer testing the beta there never sees a control that rewrites
 * their account flag.
 */
import { computed } from 'vue'
import { useNewDesign } from '@/hooks/useNewDesign'
import { useUserStore } from '@/stores/UserStore'
import { isLocalhost, isStaging } from '@/helpers/featureFlags'

const { newDesignTourSeen, toggleNewDesignTourSeen, isNewDesign } = useNewDesign()
const userStore = useUserStore()

const STAGING_TOUR_TESTERS = ['sofia@meremortals.ai']

const visible = computed(() => {
  if (isLocalhost()) return true
  if (!isStaging()) return false

  return STAGING_TOUR_TESTERS.includes((userStore.user?.email || '').toLowerCase())
})

const handleToggle = async () => {
  await toggleNewDesignTourSeen()

  // Turning the flag OFF should show the tour now, not on the next visit — it
  // starts when the user lands on new-design Inventory, so go there.
  if (!newDesignTourSeen.value) window.location.assign('/inventory')
}
</script>

<template>
  <button
    v-if="visible"
    type="button"
    class="hidden lg:flex items-center gap-x-1.5 rounded-md border border-white/40 px-2 py-1 text-xs font-medium text-white/90 transition hover:bg-white/10"
    :title="isNewDesign
      ? 'Dev only — flip new_design_tour_seen to replay the tour'
      : 'Dev only — the tour runs in the NEW design; switch designs first'"
    @click="handleToggle"
  >
    <Icon icon="heroicons:academic-cap-16-solid" class="text-sm" />
    Tour: {{ newDesignTourSeen ? 'seen' : 'pending' }}
  </button>
</template>
